import React from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {selectIsAuthenticated, selectAccessToken} from '@components/Login/userSlice';
import {LOGIN_PATH, UNAUTHORIZED_PATH, ACCOUNT_PATH, ACCOUNT_TWO_FACTOR_AUTH_PATH, ACCESS_DENIED_PATH} from '@constants/index';
import {getIsRecipient} from '@utils/userRole';
import permissionsService from '@utils/permissionsService';
import {useGet2FAEnforcementStatus} from '@hooks/useGet2FAEnforcementStatus';

const TWO_FACTOR_SETUP_PATH = `${ACCOUNT_PATH}/${ACCOUNT_TWO_FACTOR_AUTH_PATH}`;

interface ProtectedRouteProps {
    children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({children}) => {
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const accessToken = useSelector(selectAccessToken);
    const location = useLocation();
    const {isBlocking} = useGet2FAEnforcementStatus();

    if (!isAuthenticated || !accessToken) {
        const isRecipient = getIsRecipient(accessToken);
        if (isRecipient) {
            return <Navigate to={`${UNAUTHORIZED_PATH}/recipient`} replace />;
        }
        const currentPath = location.pathname + location.search;
        const hasTarget = currentPath && currentPath !== '/' && currentPath !== LOGIN_PATH;
        const loginUrl = hasTarget
            ? `${LOGIN_PATH}?target=${encodeURIComponent(currentPath)}`
            : LOGIN_PATH;
        return <Navigate to={loginUrl} replace />;
    }

    if (isBlocking && !location.pathname.startsWith(TWO_FACTOR_SETUP_PATH)) {
        return <Navigate to={TWO_FACTOR_SETUP_PATH} replace />;
    }

    if (permissionsService.getIsScopedUser({accessToken})
        && !permissionsService.getIsPathAllowedForScopedUser({pathname: location.pathname, accessToken})) {
        return <Navigate to={ACCESS_DENIED_PATH} replace />;
    }

    return children;
};
